<template>
  <div class="email-step">
    <Heading :level="3"> Acessar conta </Heading>
    <BaseText size="p6" :color="NebraskaColors.elementSecondary">
      Caso ainda não possua conta.
      <Link class="form-link" @click="redirectToSignup">Crie sua conta.</Link>
    </BaseText>
    <form class="email-step__form" @submit.prevent="validate">
      <InputText
        id="email"
        v-model="form.email"
        class="form-input"
        label="E-mail"
        required
        leading-icon="ED0070"
        :status="getInputStatus('email')"
        :helper-text="getErrorMessage('email')"
        @input="clearErrors()"
        @blur="trackEmailEvent"
      />
      <div class="form-buttons">
        <ButtonPrimary
          id="submit"
          label="Continuar"
          :disabled="disableButton"
          :loading="isLoading"
          size="large"
          data-testid="submit-button"
          type="submit"
        />
      </div>
    </form>
  </div>
</template>
<script setup lang="ts">
import {
  BaseText,
  ButtonPrimary,
  Heading,
  InputText,
  Link
} from '@warrenbrasil/nebraska-web';
import { computed, inject, ref, Ref } from 'vue';
import { ValidationError } from 'yup';
import { useRouter } from 'vue-router/composables';
import {
  SignInForm,
  YupError
} from '@/modules/authentication/views/SignIn/types';
import { InputStatusType } from '@/modules/onboarding/signup/types';
import { signinEmailSchema } from '@/modules/authentication/views/SignIn/helpers/validate';
import {
  trackEmailEvent,
  trackSignupLink
} from '@/modules/authentication/helpers/track-signin-events';
import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';

const setTheme = inject('setTheme') as (
  themeParam: string,
  paramType: 'domain' | 'email'
) => void;
const handleEmailSubmit = inject('handleEmailSubmit') as (
  email: string
) => void;
const errors = ref<YupError[]>([]);
const router = useRouter();
const isLoading = ref<boolean>(false);
const form = inject('form') as Ref<SignInForm>;

const disableButton = computed(() => errors.value.length > 0);

async function validate() {
  isLoading.value = true;
  try {
    clearErrors();
    await signinEmailSchema.validate(form.value, { abortEarly: false });
    setTheme(form.value.email, 'email');
    handleEmailSubmit(form.value.email);
  } catch (err) {
    if (err instanceof ValidationError) {
      errors.value = err.inner.map(error => ({
        key: error.path as string,
        value: error.message
      }));
    }
    isLoading.value = false;
  }
}

function getInputStatus(key: string) {
  return errors.value.find(error => error.key === key)
    ? InputStatusType.Error
    : InputStatusType.Default;
}

function getErrorMessage(key: string) {
  return errors.value.find(error => error.key === key)?.value || '';
}

function clearErrors() {
  errors.value = [];
}

function redirectToSignup() {
  trackSignupLink();
  router.push({ name: 'signup' });
}
</script>
<style lang="less" scoped>
.email-step {
  margin-top: @size-spacing-x150;

  &__form {
    display: flex;
    flex-direction: column;
    margin-top: @size-spacing-x600;
    gap: @size-spacing-x600;
  }
}
.form-buttons {
  display: flex;
  gap: @size-spacing-x350;
  border-radius: @size-radius-x300;

  button {
    width: 100%;
  }
}
</style>
